// src/pages/storePages/manageStore/ManageStoreRoot.js
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { storeGetRootCategories } from "../../../storeAPIs/StoreCategoriesApis";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import NewCategoryDialog from "./NewCategoryDialog";
import DeleteCategoryDialog from "./DeleteCategoryDialog";
import "../../../static/storecss/manageStore/shared.css";
import "../../../static/storecss/manageStore/ManageStoreRoot.css";

export default function ManageStoreRoot() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetcher = useCallback(
    (query, signal) =>
      storeGetRootCategories({ search: query || undefined }, { signal }),
    []
  );

  const {
    query,
    setQuery,
    results: roots,
    setResults,
    count,
    setCount,
    next,
    loading,
    error,
    loadMore,
    reset,
  } = useDebouncedSearch(fetcher);

  const handleSaved = (saved, mode) => {
    if (!saved?.id) {
      reset();
      return;
    }
    if (mode === "created") {
      setResults((prev) => [saved, ...prev]);
      setCount((prev) => prev + 1);
    } else {
      setResults((prev) =>
        prev.map((c) => (c.id === saved.id ? { ...c, ...saved } : c))
      );
    }
  };

  const handleDeleted = (deleted) => {
    setResults((prev) => prev.filter((c) => c.id !== deleted.id));
    setCount((prev) => Math.max(0, prev - 1));
  };

  return (
    <div className="msr-page">
      <header className="msr-header">
        <div className="msr-header-text">
          <h1>Store</h1>
          <p>Manage categories and products.</p>
        </div>
        <button
          type="button"
          className="msr-btn msr-btn--primary"
          onClick={() => setDialogOpen(true)}
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
          <span>New category</span>
        </button>
      </header>

      <div className="msr-toolbar">
        <div className="msr-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search categories…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="msr-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          )}
        </div>

        {!loading && roots.length > 0 && (
          <span className="msr-count">
            {count} {count === 1 ? "category" : "categories"}
          </span>
        )}
      </div>

      {error && <div className="msr-alert">{error}</div>}

      {loading && roots.length === 0 ? (
        <div className="msr-loading">
          <span className="msr-spinner" />
          <span>Loading…</span>
        </div>
      ) : roots.length === 0 ? (
        <div className="msr-empty">
          <i className="fa-regular fa-folder-open" aria-hidden="true" />
          <h2>{query ? "No matches" : "No categories yet"}</h2>
          <p>
            {query
              ? `Nothing matches "${query}". Try a different search.`
              : "Create your first category to start organizing products."}
          </p>
          {query ? (
            <button className="msr-btn msr-btn--secondary" onClick={reset}>
              Clear search
            </button>
          ) : (
            <button
              type="button"
              className="msr-btn msr-btn--primary"
              onClick={() => setDialogOpen(true)}
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
              <span>New category</span>
            </button>
          )}
        </div>
      ) : (
        <ul className="msr-list">
          {roots.map((cat) => {
            const childCount = cat.children?.length ?? 0;
            const productCount = cat.product_count ?? 0;

            return (
              <li key={cat.id} className="msr-row-wrap">
                <Link
                  to={`/manage-store/category/${cat.slug}`}
                  className="msr-row"
                  data-cat-id={cat.id}
                >
                  <div className="msr-row-main">
                    <div className="msr-row-title">
                      <span className="msr-row-name">{cat.name}</span>
                      <code className="msr-row-slug">{cat.slug}</code>
                    </div>
                    <div className="msr-row-meta">
                      <span>{childCount} subcategories</span>
                      <span className="msr-dot" aria-hidden="true" />
                      <span>{productCount} products</span>
                    </div>
                  </div>

                  <i
                    className="fa-solid fa-chevron-right msr-row-chevron"
                    aria-hidden="true"
                  />
                </Link>

                <button
                  type="button"
                  className="msr-row-delete"
                  onClick={() => setPendingDelete(cat)}
                  aria-label={`Delete ${cat.name}`}
                  title="Delete"
                >
                  <i className="fa-solid fa-trash" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {next && (
        <div className="msr-load-more">
          <button
            className="msr-btn msr-btn--secondary"
            onClick={loadMore}
            disabled={loading}
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      <NewCategoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <DeleteCategoryDialog
        open={!!pendingDelete}
        category={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={handleDeleted}
      />
    </div>
  );
}